/**
 * Knowledge Graph Import Messages for Finbot Chatbot
 * i18n wrapper for Knowledge Graph operations
 *
 * This file provides backwards-compatible exports using i18n translations
 */

import i18n from 'i18next';

/**
 * Helper to get translation - lazy evaluation to ensure i18n is initialized
 * Keys are in admin namespace, so we need to prefix them
 */
const t = (key: string, params?: any): string => {
  if (!i18n.isInitialized) {
    console.warn('[kgMessages] i18n not initialized yet, returning key:', key);
    return key;
  }
  // Keys are already prefixed with 'content.' which is in admin namespace
  return i18n.t(key, { ...params, ns: 'admin' }) as string;
};

/**
 * Progress messages - maps to content.knowledgeGraph.buildManager.progressTracker.*
 */
export const KG_PROGRESS_MESSAGES = {
  // Progress states
  get initializing(): string { return t('content.knowledgeGraph.buildManager.progressTracker.progress.initializing'); },
  get polling(): string { return t('content.knowledgeGraph.buildManager.progressTracker.progress.polling'); },
  get completed(): string { return t('content.knowledgeGraph.buildManager.progressTracker.progress.completed'); },
  get failed(): string { return t('content.knowledgeGraph.buildManager.progressTracker.progress.failed'); },
  get cancelled(): string { return t('content.knowledgeGraph.buildManager.progressTracker.progress.cancelled'); },
  get skipped(): string { return t('content.knowledgeGraph.buildManager.progressTracker.progress.skipped'); },
  get networkError(): string { return t('content.knowledgeGraph.buildManager.progressTracker.progress.networkError'); },
  get timeout(): string { return t('content.knowledgeGraph.buildManager.progressTracker.progress.timeout'); },
  get retrying(): string { return t('content.knowledgeGraph.buildManager.progressTracker.progress.retrying'); },

  // Import stages
  get validatingInputs(): string { return t('content.knowledgeGraph.buildManager.progressTracker.stages.validatingInputs'); },
  get initializingManager(): string { return t('content.knowledgeGraph.buildManager.progressTracker.stages.initializingManager'); },
  get executingSyncBuild(): string { return t('content.knowledgeGraph.buildManager.progressTracker.stages.executingSyncBuild'); },
  get verifyingConsistency(): string { return t('content.knowledgeGraph.buildManager.progressTracker.stages.verifyingConsistency'); },
  get preparingWorkers(): string { return t('content.knowledgeGraph.buildManager.progressTracker.stages.preparingWorkers'); },
  get backgroundProcessing(): string { return t('content.knowledgeGraph.buildManager.progressTracker.stages.backgroundProcessing'); },
  get finalizing(): string { return t('content.knowledgeGraph.buildManager.progressTracker.stages.finalizing'); },

  // Status updates
  get importRunning(): string { return t('content.knowledgeGraph.buildManager.progressTracker.status.importRunning'); },
  get importCompleted(): string { return t('content.knowledgeGraph.buildManager.progressTracker.status.importCompleted'); },
  get importFailed(): string { return t('content.knowledgeGraph.buildManager.progressTracker.status.importFailed'); },
  get importCancelled(): string { return t('content.knowledgeGraph.buildManager.progressTracker.status.importCancelled'); },
  get importSkipped(): string { return t('content.knowledgeGraph.buildManager.progressTracker.status.importSkipped'); },

  // Progress notifications
  get significantProgress(): string { return t('content.knowledgeGraph.buildManager.progressTracker.notifications.significantProgress'); },
  get importStarted(): string { return t('content.knowledgeGraph.buildManager.progressTracker.notifications.importStarted'); },
  get importCancelSuccess(): string { return t('content.knowledgeGraph.buildManager.progressTracker.notifications.importCancelSuccess'); },
  get importCancelFailed(): string { return t('content.knowledgeGraph.buildManager.progressTracker.notifications.importCancelFailed'); },

  // Detailed messages
  get documentsProcessed(): string { return t('content.knowledgeGraph.buildManager.progressTracker.details.documentsProcessed'); },
  get nodesCreated(): string { return t('content.knowledgeGraph.buildManager.progressTracker.details.nodesCreated'); },
  get relationshipsCreated(): string { return t('content.knowledgeGraph.buildManager.progressTracker.details.relationshipsCreated'); },
  get elapsedTime(): string { return t('content.knowledgeGraph.buildManager.progressTracker.details.elapsedTime'); },
  get estimatedTimeRemaining(): string { return t('content.knowledgeGraph.buildManager.progressTracker.details.estimatedTimeRemaining'); },

  // Error messages
  get noAuthToken(): string { return t('content.knowledgeGraph.buildManager.progressTracker.errors.noAuthToken'); },
  get tooManyErrors(): string { return t('content.knowledgeGraph.buildManager.progressTracker.errors.tooManyErrors'); },
  get cancelFailed(): string { return t('content.knowledgeGraph.buildManager.progressTracker.errors.cancelFailed'); },
  get retryFailed(): string { return t('content.knowledgeGraph.buildManager.progressTracker.errors.retryFailed'); },

  // Success messages
  get kgImported(): string { return t('content.knowledgeGraph.buildManager.progressTracker.notifications.kgImported'); },
  get cancelCompleted(): string { return t('content.knowledgeGraph.buildManager.progressTracker.notifications.cancelCompleted'); },

  // UI messages
  get showDetails(): string { return t('content.knowledgeGraph.buildManager.progressTracker.ui.showDetails'); },
  get hideDetails(): string { return t('content.knowledgeGraph.buildManager.progressTracker.ui.hideDetails'); },
  get expand(): string { return t('content.knowledgeGraph.buildManager.progressTracker.ui.expand'); },
  get compact(): string { return t('content.knowledgeGraph.buildManager.progressTracker.ui.compact'); },
  get currentStep(): string { return t('content.knowledgeGraph.buildManager.progressTracker.ui.currentStep'); },
  get loadingProgress(): string { return t('content.knowledgeGraph.buildManager.progressTracker.ui.loadingProgress'); },
  get autoDismiss(): string { return t('content.knowledgeGraph.buildManager.progressTracker.ui.autoDismiss'); },
  get seconds(): string { return t('content.knowledgeGraph.buildManager.progressTracker.ui.seconds'); },
  get quickActions(): string { return t('content.knowledgeGraph.buildManager.progressTracker.ui.quickActions'); },
  get error(): string { return t('content.knowledgeGraph.buildManager.progressTracker.ui.error'); }
};

/**
 * Error messages - maps to content.knowledgeGraph.buildManager.kgErrors.*
 */
export const KG_ERROR_MESSAGES = {
  get selectKgFile(): string { return t('content.knowledgeGraph.buildManager.kgErrors.selectKgFile'); },
  get validationFailed(): string { return t('content.knowledgeGraph.buildManager.kgErrors.validationFailed'); },
  get validateFirst(): string { return t('content.knowledgeGraph.buildManager.kgErrors.validateFirst'); },
  get importFailed(): string { return t('content.knowledgeGraph.buildManager.kgErrors.importFailed'); },
  get exportFailed(): string { return t('content.knowledgeGraph.buildManager.kgErrors.exportFailed'); },
  get networkError(): string { return t('content.knowledgeGraph.buildManager.kgErrors.networkError'); },
  get maxRetriesReached(): string { return t('content.knowledgeGraph.buildManager.kgErrors.maxRetriesReached'); },
  get cannotStartTracking(): string { return t('content.knowledgeGraph.buildManager.kgErrors.cannotStartTracking'); },
  get pollingFailed(): string { return t('content.knowledgeGraph.buildManager.kgErrors.pollingFailed'); },
  get duplicateKG(): string { return t('content.knowledgeGraph.buildManager.kgErrors.duplicateKG'); },
  get importSkipped(): string { return t('content.knowledgeGraph.buildManager.kgErrors.importSkipped'); }
};

/**
 * Success messages - maps to content.knowledgeGraph.buildManager.kgSuccess.*
 */
export const KG_SUCCESS_MESSAGES = {
  get validationSuccess(): string { return t('content.knowledgeGraph.buildManager.kgSuccess.validationSuccess'); },
  get filesReady(): string { return t('content.knowledgeGraph.buildManager.kgSuccess.filesReady'); },
  get importComplete(): string { return t('content.knowledgeGraph.buildManager.kgSuccess.importComplete'); },
  get kgImported(): string { return t('content.knowledgeGraph.buildManager.kgSuccess.kgImported'); },
  get exportStarted(): string { return t('content.knowledgeGraph.buildManager.kgSuccess.exportStarted'); },
  get exportReady(): string { return t('content.knowledgeGraph.buildManager.kgSuccess.exportReady'); },
  get exportComplete(): string { return t('content.knowledgeGraph.buildManager.kgSuccess.exportComplete'); },
  get kgExported(): string { return t('content.knowledgeGraph.buildManager.kgSuccess.kgExported'); }
};

/**
 * Warning messages - maps to content.knowledgeGraph.buildManager.kgWarnings.*
 */
export const KG_WARNING_MESSAGES = {
  get validationIssues(): string { return t('content.knowledgeGraph.buildManager.kgWarnings.validationIssues'); },
  get errorsNeedResolving(): string { return t('content.knowledgeGraph.buildManager.kgWarnings.errorsNeedResolving'); },
  get activeImportWarning(): string { return t('content.knowledgeGraph.buildManager.kgWarnings.activeImportWarning'); },
  get cannotStartNewImport(): string { return t('content.knowledgeGraph.buildManager.kgWarnings.cannotStartNewImport'); },
  get duplicateContentDetected(): string { return t('content.knowledgeGraph.buildManager.kgWarnings.duplicateContentDetected'); },
  get kgAlreadyExists(): string { return t('content.knowledgeGraph.buildManager.kgWarnings.kgAlreadyExists'); }
};

/**
 * Status labels - maps to content.knowledgeGraph.buildManager.kgStatusLabels.*
 */
export const KG_STATUS_LABELS = {
  get pending(): string { return t('content.knowledgeGraph.buildManager.kgStatusLabels.pending'); },
  get running(): string { return t('content.knowledgeGraph.buildManager.kgStatusLabels.running'); },
  get completed(): string { return t('content.knowledgeGraph.buildManager.kgStatusLabels.completed'); },
  get failed(): string { return t('content.knowledgeGraph.buildManager.kgStatusLabels.failed'); },
  get cancelled(): string { return t('content.knowledgeGraph.buildManager.kgStatusLabels.cancelled'); },
  get skipped(): string { return t('content.knowledgeGraph.buildManager.kgStatusLabels.skipped'); }
};

/**
 * Action labels - maps to content.knowledgeGraph.buildManager.kgActions.*
 */
export const KG_ACTIONS = {
  get validate(): string { return t('content.knowledgeGraph.buildManager.kgActions.validate'); },
  get import(): string { return t('content.knowledgeGraph.buildManager.kgActions.import'); },
  get cancel(): string { return t('content.knowledgeGraph.buildManager.kgActions.cancel'); },
  get retry(): string { return t('content.knowledgeGraph.buildManager.kgActions.retry'); },
  get export(): string { return t('content.knowledgeGraph.buildManager.kgActions.export'); },
  get reset(): string { return t('content.knowledgeGraph.buildManager.kgActions.reset'); },
  get viewDetails(): string { return t('content.knowledgeGraph.buildManager.kgActions.viewDetails'); },
  get refresh(): string { return t('content.knowledgeGraph.buildManager.kgActions.refresh'); },
  get dismiss(): string { return t('content.knowledgeGraph.buildManager.kgActions.dismiss'); },
  get markAsViewed(): string { return t('content.knowledgeGraph.buildManager.kgActions.markAsViewed'); }
};

/**
 * Metrics labels - maps to content.knowledgeGraph.buildManager.kgMetrics.*
 */
export const KG_METRICS_LABELS = {
  get documents(): string { return t('content.knowledgeGraph.buildManager.kgMetrics.documents'); },
  get nodes(): string { return t('content.knowledgeGraph.buildManager.kgMetrics.nodes'); },
  get relationships(): string { return t('content.knowledgeGraph.buildManager.kgMetrics.relationships'); },
  get time(): string { return t('content.knowledgeGraph.buildManager.kgMetrics.time'); },
  get errors(): string { return t('content.knowledgeGraph.buildManager.kgMetrics.errors'); },
  get total(): string { return t('content.knowledgeGraph.buildManager.kgMetrics.total'); },
  get success(): string { return t('content.knowledgeGraph.buildManager.kgMetrics.success'); },
  get failed(): string { return t('content.knowledgeGraph.buildManager.kgMetrics.failed'); },
  get averageTime(): string { return t('content.knowledgeGraph.buildManager.kgMetrics.averageTime'); },
  get folders(): string { return t('content.knowledgeGraph.buildManager.kgMetrics.folders'); }
};

/**
 * Import steps - maps to content.knowledgeGraph.buildManager.kgImportSteps.*
 */
export const KG_IMPORT_STEPS = {
  validatingInputs: {
    id: 'validate_data',
    get name(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.validatingInputs.name'); },
    get nameVn(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.validatingInputs.name'); },
    get description(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.validatingInputs.description'); }
  },
  backupExisting: {
    id: 'backup_existing',
    get name(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.backupExisting.name'); },
    get nameVn(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.backupExisting.name'); },
    get description(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.backupExisting.description'); }
  },
  clearExisting: {
    id: 'clear_existing',
    get name(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.clearExisting.name'); },
    get nameVn(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.clearExisting.name'); },
    get description(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.clearExisting.description'); }
  },
  importNeo4j: {
    id: 'import_neo4j_data',
    get name(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.importNeo4j.name'); },
    get nameVn(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.importNeo4j.name'); },
    get description(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.importNeo4j.description'); }
  },
  importEmbeddings: {
    id: 'import_embeddings',
    get name(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.importEmbeddings.name'); },
    get nameVn(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.importEmbeddings.name'); },
    get description(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.importEmbeddings.description'); }
  },
  createTracking: {
    id: 'create_tracking',
    get name(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.createTracking.name'); },
    get nameVn(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.createTracking.name'); },
    get description(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.createTracking.description'); }
  },
  linkDocuments: {
    id: 'link_documents',
    get name(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.linkDocuments.name'); },
    get nameVn(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.linkDocuments.name'); },
    get description(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.linkDocuments.description'); }
  },
  complete: {
    id: 'complete',
    get name(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.complete.name'); },
    get nameVn(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.complete.name'); },
    get description(): string { return t('content.knowledgeGraph.buildManager.kgImportSteps.complete.description'); }
  }
};
